<template>
  <div class="ai-feedback-container">
    <!-- AI Cursor (only rendered during an active walkthrough/feedback session) -->
    <div
      v-if="isSessionActive"
      class="ai-cursor"
      :class="{ processing: isProcessing }"
      :style="cursorStyle"
    >
      <div class="ai-cursor-ring"></div>
      <div class="ai-cursor-dot"></div>
    </div>

    <!-- Global Notification Bubble -->
    <div
      v-if="showNotification"
      class="notification-bubble"
      :class="notificationClass"
      :style="bubbleStyle"
    >
      <div class="notification-content">
        <v-icon :color="iconColor" small class="mr-2">{{ notificationIcon }}</v-icon>
        <span class="notification-message">{{ notificationMessage }}</span>
      </div>

      <!-- Progress bar showing remaining time -->
      <div class="notification-progress">
        <div
          class="notification-progress-bar"
          :style="{ width: `${remainingTimePercentage}%` }"
        ></div>
      </div>
    </div>
  </div>
</template>

<script>
import AIEnhancementService from '@/services/aiEnhancementService';
import ProgramContextService from '@/services/programContextService';
import { isCoarsePointer, toastBubbleStyle } from '@/utils/toastPosition';
import { isPreviewWriteBlocked } from '@/utils/previewGuard';

export default {
  name: 'AIFeedbackCursor',
  data() {
    return {
      isProcessing: false,
      showNotification: false,
      cursorPosition: { x: 0, y: 0 },
      hasPointer: false, // set on the first mousemove (taps also fire one, hence coarsePointer)
      coarsePointer: isCoarsePointer(), // (hover: none) and (pointer: coarse) at mount
      notificationSeq: 0, // guards a slow rephrase from overwriting a newer toast
      notificationMessage: '',
      notificationType: 'success',
      originalMessage: '', // Store original message for fallback
      aiEnhancementEnabled: true, // Toggle for AI enhancement
      notificationContext: null, // Store context for AI enhancement
      notificationDuration: 4000, // Duration in milliseconds
      remainingTime: 4000, // Remaining time in milliseconds
      progressInterval: null, // Interval for updating progress bar
      currentProgramContext: null // Store current program context
    };
  },
  computed: {
    // The simulated AI cursor should only be visible while a walkthrough/feedback
    // session is actually running. Otherwise it rests at its {0,0} start position
    // and shows as a stray blue dot in the top-left corner.
    isSessionActive() {
      return this.isProcessing || this.showNotification;
    },
    cursorStyle() {
      return {
        left: `${this.cursorPosition.x}px`,
        top: `${this.cursorPosition.y}px`
      };
    },
    bubbleStyle() {
      // Touch-primary devices (and a mouse that has not moved yet) get a top-centre
      // dock; a real mouse is followed but clamped inside the viewport. The tap-sets-
      // pointer trap and the clamp live in utils/toastPosition (unit-tested).
      return toastBubbleStyle({
        coarse: this.coarsePointer,
        pointer: this.hasPointer ? this.cursorPosition : null,
        viewport: {
          width: typeof window !== 'undefined' ? window.innerWidth : 0,
          height: typeof window !== 'undefined' ? window.innerHeight : 0
        }
      });
    },
    notificationClass() {
      return `notification-bubble--${this.notificationType}`;
    },
    notificationIcon() {
      const icons = {
        success: 'mdi-check-circle',
        error: 'mdi-alert-circle',
        info: 'mdi-information',
        warning: 'mdi-alert'
      };
      return icons[this.notificationType] || 'mdi-information';
    },
    iconColor() {
      return 'white';
    },
    remainingTimePercentage() {
      if (this.notificationDuration <= 0) return 0;
      return Math.max(0, (this.remainingTime / this.notificationDuration) * 100);
    }
  },
  mounted() {
    // Check if AI enhancement is properly configured
    this.checkAIConfiguration();

    // Start cursor tracking and store listening
    this.trackCursor();
    this.listenToStore();

    // Initialize program context
    this.updateProgramContext();
  },

  beforeDestroy() {
    // Clean up intervals to prevent memory leaks
    this.stopProgressBar();
  },
  methods: {
    checkAIConfiguration() {
      // Rephrasing goes through the Nexus OpenAI proxy (openaiService.generateScope), so
      // a client-side key is not required. Kept as a log line only; the service itself
      // falls back to the plain message on any failure.
      console.log('✅ AI Enhancement: enabled (Nexus proxy, plain-copy fallback)');
    },
    trackCursor() {
      document.addEventListener('mousemove', e => {
        this.hasPointer = true;
        this.cursorPosition = { x: e.clientX, y: e.clientY };
      });
    },
    updateProgramContext() {
      // Update program context when component mounts or program changes
      if (ProgramContextService.isUserInProgram()) {
        this.currentProgramContext = ProgramContextService.getCurrentProgramContext();
        console.log('🔍 AIFeedbackCursor: Program context updated:', {
          programName: this.currentProgramContext.programName,
          programDesc: this.currentProgramContext.programDesc
        });
      }
    },
    listenToStore() {
      // Listen to store changes for notifications
      const store = this.$store;

      console.log('🔍 AIFeedbackCursor: Setting up store listener');

      // Watch for snackbar state changes
      this.$watch(
        () => store.state.tools.snackBar,
        newSnackbar => {
          console.log('🔍 AIFeedbackCursor: Store changed:', newSnackbar);
          if (newSnackbar.isShowSnackbar && newSnackbar.message) {
            console.log('🔍 AIFeedbackCursor: Showing notification:', newSnackbar.message);
            this.displayNotification(newSnackbar.message, newSnackbar.type, newSnackbar.context);
            // Clear the store state
            store.dispatch('tools/showSnackbar', {
              isShowSnackbar: false,
              type: '',
              message: '',
              context: null
            });
          }
        },
        { deep: true, immediate: true }
      );

      // Watch for program changes to update context
      this.$watch(
        () => store.state.user.programDoc,
        () => {
          this.updateProgramContext();
        },
        { deep: true }
      );
    },
    async displayNotification(message, type = 'success', context = null) {
      this.originalMessage = message;
      this.notificationType = type;
      this.notificationContext = context;
      this.notificationSeq = (this.notificationSeq || 0) + 1;
      const seq = this.notificationSeq;

      // Show the plain message IMMEDIATELY. The bubble used to stay hidden while the
      // AI rephrase was awaited, so a rate-limited or slow call meant the student saw
      // nothing at all for the action they just took. If the rephrase arrives quickly
      // (see enhanceMessageWithAI) it swaps in place; otherwise the plain copy stands.
      this.notificationMessage = message;
      this.showNotification = true;

      // Calculate dynamic duration based on message length
      const duration = this.calculateNotificationDuration(this.notificationMessage);
      this.notificationDuration = duration;
      this.remainingTime = duration;

      // Start progress bar animation
      this.startProgressBar();

      // Hide after calculated duration
      setTimeout(() => {
        if (this.notificationSeq !== seq) return; // a newer toast owns the bubble
        this.showNotification = false;
        this.stopProgressBar();
      }, duration);

      // A reader behind the write fence (preview link, someone else's public page) gets
      // the plain copy: their clicks must not spend an AI call either.
      if (context && this.aiEnhancementEnabled && !isPreviewWriteBlocked()) {
        this.isProcessing = true;
        await this.enhanceMessageWithAI(message, type, context, seq);
      }
    },

    async enhanceMessageWithAI(originalMessage, type, context = null, seq = undefined) {
      try {
        // Auto-include program context if available
        let enhancedContext = { ...context };
        if (this.currentProgramContext) {
          enhancedContext = {
            ...context,
            programContext: {
              programId: this.currentProgramContext.programId,
              programName: this.currentProgramContext.programName,
              programDesc: this.currentProgramContext.programDesc,
              pathways: this.currentProgramContext.pathways,
              industries: this.currentProgramContext.industries
            }
          };

          console.log('🔍 AIFeedbackCursor: Enhanced context with program info:', {
            programName: this.currentProgramContext.programName,
            programDesc: this.currentProgramContext.programDesc
          });
        }

        // Enhance the message with AI
        const result = await AIEnhancementService.enhanceNotification({
          message: originalMessage,
          type,
          context: enhancedContext
        });

        // Only swap the text if THIS toast is still the one on screen and it is early
        // enough that the swap reads as the same message, not a second one.
        const stillCurrent =
          (seq === undefined || seq === this.notificationSeq) &&
          this.showNotification &&
          this.originalMessage === originalMessage;
        const earlyEnough = this.remainingTime > this.notificationDuration * 0.4;
        if (result.success && stillCurrent && earlyEnough) {
          this.notificationMessage = result.enhancedMessage;
          console.log('🔍 AIFeedbackCursor: Enhanced message:', result.enhancedMessage);
        } else if (!result.success) {
          console.warn('🔍 AIFeedbackCursor: AI enhancement skipped, plain message kept');
        }
      } catch (error) {
        console.error('🔍 AIFeedbackCursor: AI enhancement error:', error);
        // Plain message is already on screen; nothing to restore
      } finally {
        // Hide processing state
        this.isProcessing = false;
      }
    },

    calculateNotificationDuration(message) {
      if (!message) return 3000; // Default 3 seconds for empty messages

      // Count characters (excluding HTML tags if any)
      const textLength = message.replace(/<[^>]*>/g, '').length;

      // Base duration: 3 seconds minimum
      const baseDuration = 3000;

      // Additional time per character: 50ms per character
      const additionalTime = textLength * 50;

      // Maximum duration: 12 seconds
      const maxDuration = 12000;

      // Calculate final duration
      const duration = Math.min(baseDuration + additionalTime, maxDuration);

      console.log(
        `🔍 AIFeedbackCursor: Message length: ${textLength} chars, Duration: ${duration}ms`
      );

      return duration;
    },

    startProgressBar() {
      // Clear any existing interval
      this.stopProgressBar();

      // Update progress every 50ms for smooth animation
      this.progressInterval = setInterval(() => {
        this.remainingTime = Math.max(0, this.remainingTime - 50);
      }, 50);
    },

    stopProgressBar() {
      if (this.progressInterval) {
        clearInterval(this.progressInterval);
        this.progressInterval = null;
      }
    },

    simulateAIProcessing() {
      // Simulate AI processing for 3 seconds
      this.isProcessing = true;

      setTimeout(() => {
        this.isProcessing = false;
        this.displayNotification('AI feedback generated successfully!', 'success');
      }, 3000);
    }
  }
};
</script>

<style scoped>
.ai-feedback-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 9999;
}

.ai-cursor {
  position: absolute;
  pointer-events: none;
  opacity: 0.3;
  transition: opacity 0.3s ease;
}

.ai-cursor:hover {
  opacity: 0.6;
}

.ai-cursor.processing {
  opacity: 1;
}

.ai-cursor-ring {
  position: absolute;
  top: 18px;
  left: 5px;
  width: 40px;
  height: 40px;
  border: 2px solid #3c9dcd;
  border-radius: 50%;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.ai-cursor.processing .ai-cursor-ring {
  opacity: 1;
  animation: pulse 1.5s ease-in-out infinite;
}

.ai-cursor-dot {
  position: absolute;
  top: 25px;
  left: 15px;
  width: 8px;
  height: 8px;
  background: #3c9dcd;
  border-radius: 50%;
  opacity: 1;
  transition: opacity 0.3s ease;
}

.ai-cursor.processing .ai-cursor-dot {
  opacity: 1;
  animation: blink 0.8s ease-in-out infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.7;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes blink {
  0%,
  50% {
    opacity: 1;
  }
  51%,
  100% {
    opacity: 0.3;
  }
}

.notification-bubble {
  position: absolute;
  max-width: 300px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border: 1px solid #dedede; /* lightgrey */
  animation: expandBubble 0.3s ease-out;
  pointer-events: none;
  position: relative;
}

/* Palette (plugins/vuetify.ts): each type = its PilotCity colour fading to the 40%-darker
   step of the same hue, edge in that darker step. The old mix of PilotCity + Material
   colours (#4caf50, #f44336, #ff9800, #2196f3) put two greens / reds / blues side by side. */
.notification-bubble--success {
  background: linear-gradient(135deg, #6eba80, #4f9863); /* green -> toward darkgreen */
  border-left: 4px solid #3b6b48; /* darkgreen */
}

.notification-bubble--error {
  background: linear-gradient(135deg, #ea6764, #c4514e); /* red -> toward darkred */
  border-left: 4px solid #8f3b38; /* darkred */
}

.notification-bubble--warning {
  background: linear-gradient(135deg, #fec34b, #d9a334); /* yellow -> toward darkyellow */
  border-left: 4px solid #b3861e; /* darkyellow */
}

.notification-bubble--info {
  background: linear-gradient(135deg, #3c9dcd, #3081ab); /* blue -> toward darkblue */
  border-left: 4px solid #24648a; /* darkblue */
}

@keyframes expandBubble {
  from {
    opacity: 0;
    transform: scale(0.8) translateY(10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.notification-content {
  display: flex;
  align-items: center;
  padding: 12px;
  gap: 8px;
}

.notification-message {
  font-size: 14px;
  line-height: 1.4;
  color: white;
  font-weight: 500;
}

.notification-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 3px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 0 0 12px 12px;
  overflow: hidden;
}

.notification-progress-bar {
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  transition: width 0.05s linear;
  border-radius: 0 0 12px 12px;
}
</style>
